import { defineComponent as _defineComponent } from 'vue'
import ButtonCommon from '../buttons/ButtonCommon.vue';
import { computed, ref } from 'vue';
import draggable from "vuedraggable";
import SearchBar from "../text-inputs/searchBar/SearchBar.vue";
import * as types from "./types";
import LoadingSpinner from "../../components/loaders/LoadingSpinner.vue";
import { IntersectionPaginationOption, IntersectionStateChanger } from '@/utils/types';

type OperationJSON = {
  [key in "added" | "removed" | "moved"]: {element: types.ArrayValue, oldIndex: number};
};


export default /*#__PURE__*/_defineComponent({
  __name: 'DragAndDrop',
  props: {
    list: null,
    value: null,
    rightTitle: null,
    leftTitle: null,
    addAllText: { default: '' },
    removeAllText: { default: '' },
    placeholderLeft: { default: '' },
    placeholderRight: { default: '' },
    fullWidth: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false },
    searchPlaceholders: { default: () => ({
      right: '',
      left: '',
    }) },
    bulkAction: { type: Boolean, default: true },
    paginated: { type: Boolean, default: false },
    disableDrag: { type: Boolean, default: false },
    noSearchResultText: null
  },
  emits: ["input", "infinite-left", "infinite-right", "search-left", "search-right", "data-changed"],
  setup(__props: any, { emit }: { emit: ({
  (e: "input", value: types.ArrayValue[]): void;
  (e: "infinite-left", value: {search: string, state: IntersectionStateChanger}): void;
  (e: "infinite-right", value: {search: string, state: IntersectionStateChanger}): void;
  (e: "search-left", value: {search: string, state: IntersectionStateChanger}): void;
  (e: "search-right", value: {search: string, state: IntersectionStateChanger}): void;
  (e: "data-changed", value: {added: types.ArrayValue[], removed: types.ArrayValue[]}): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { list: types.ArrayValue[], value: types.ArrayValue[], rightTitle: string, leftTitle: string, addAllText: string, removeAllText: string, placeholderLeft: string, placeholderRight: string, fullWidth: boolean, searchable: boolean, searchPlaceholders: types.SearchPlaceholders, bulkAction: boolean, paginated: boolean, disableDrag: boolean, noSearchResultText?: string };





const isHovered = ref<boolean>(true)
const currentSearchLeft = ref<string>('');
const currentSearchRight = ref<string>('');
const visibilityPlanLeft = ref<Set<types.ArrayValue>>(new Set([]));
const visibilityPlanRight = ref<Set<types.ArrayValue>>(new Set([]));

const listValueRef = ref<types.ArrayValue[]>(props.list);
const valueListRef = ref<types.ArrayValue[]>(props.value);

const listValue = computed({
  get: () => {
    if(!props.paginated) {
      return listValueRef.value;
    }
    const combinedArr = [...addedItems.value.filter((item) => item.text.toLowerCase().indexOf(currentSearchLeft.value.toLowerCase()) !== -1), ...props.list ];
    return combinedArr.filter((item) => !removedItems.value.some((removeItem) => removeItem.value === item.value));
  },
  set:(val) => {
    if(!props.paginated) {
      listValueRef.value = val;
    }
  }
});	
const valueList = computed({
  get: () => {
    if(!props.paginated) {
      return valueListRef.value;
    }
  const combinedArr = [...removedItems.value.filter((item) => item.text.toLowerCase().indexOf(currentSearchRight.value.toLowerCase()) !== -1), ...props.value ];
  return combinedArr.filter((item) => !addedItems.value.some((addedItem) => addedItem.value === item.value));
  },
  set: (val) => {
    if(!props.paginated) {
      valueListRef.value = val;
    }
  }
});
const allItems = computed(()=> [ ...valueList.value, ...listValue.value ])
const listValueSearch = computed(() => {
  return props.paginated ? listValue.value 
  : listValue.value.map((item) => ({
    ...item,
    isHidden: !visibilityPlanLeft.value.has(item) && !!currentSearchLeft.value
  }))
});
const valueListSearch = computed(() => {
  return props.paginated ? valueList.value 
  : valueList.value.map((item) => ({
    ...item,
    isHidden: !visibilityPlanRight.value.has(item) && !!currentSearchRight.value
  }))
});

const isListValueEmpty = computed(() => (listValue.value.length === 0));
const isValueListEmpty = computed(() => (valueList.value.length === 0));

const paginationLeftRef = ref<IntersectionPaginationOption>({
  loaded: false,
  completed: false,
})

const paginationRightRef = ref<IntersectionPaginationOption>({
  loaded: false,
  completed: false,
})

const stateLeft: IntersectionStateChanger = {
  loaded: () => {
    paginationLeftRef.value.loaded = true;
  },
  completed: () => {
    paginationLeftRef.value.completed = true;
  },
  reset: () => {
    paginationLeftRef.value.loaded = false;
    paginationLeftRef.value.completed = false;
  }
};

const stateRight: IntersectionStateChanger = {
  loaded: () => {
    paginationRightRef.value.loaded = true;
  },
  completed: () => {
    paginationRightRef.value.completed = true;
  },
  reset: () => {
    paginationRightRef.value.loaded = false;
    paginationRightRef.value.completed = false;
  }
};
let bounceTimer: ReturnType<typeof setTimeout> | undefined
const addedItems = ref<types.ArrayValue[]>([]);
const removedItems = ref<types.ArrayValue[]>([]);

function leftSideHandleIntersection(entries: IntersectionObserverEntry[], observer: IntersectionObserver, isIntersecting: boolean) {
  if(isIntersecting) {
    if(paginationLeftRef.value.completed) return;
    paginationLeftRef.value.loaded = false;
    emit('infinite-left', {search: currentSearchLeft.value, state: stateLeft});
  }
}

function rightSideHandleIntersection(entries: IntersectionObserverEntry[], observer: IntersectionObserver, isIntersecting: boolean) {
  if(isIntersecting) {
    if(paginationRightRef.value.completed) return;
    paginationRightRef.value.loaded = false;
    emit('infinite-right', {search: currentSearchRight.value, state: stateRight});
  }
}

function elementAddedRemoved(action: OperationJSON) {
  if(!props.paginated) {
    change();
    return;
  }
  const eventType = Object.keys(action)[0];

  if(eventType === "added") {
    const index = removedItems.value.findIndex(element => element.value === action.added.element.value);
    if (index !== -1) {
      removedItems.value.splice(index, 1);
    } else {
      addedItems.value.push(action["added"].element);
    }
  } else if(eventType === "removed") {
    const index = addedItems.value.findIndex(element => element.value === action.removed.element.value);
    if (index !== -1) {
      addedItems.value.splice(index, 1);
    } else {
      removedItems.value.push(action["removed"].element);
    }
  }
  change();
  emit("data-changed", {added: addedItems.value, removed: removedItems.value});
}


function elementClicked(element: types.ArrayValue, type: "left" | "right") {
  if(!props.paginated) {
    return;
  }
  if(type === "left") {
    const index = addedItems.value.findIndex(item => item.value === element.value);
    if (index !== -1) {
      addedItems.value.splice(index, 1);
    } else {
      removedItems.value.unshift(element);
    }
  } else {
    const index = removedItems.value.findIndex(item => item.value === element.value);
    if (index !== -1) {
      removedItems.value.splice(index, 1);
    } else {
      addedItems.value.unshift(element);
    }
  }
  emit("data-changed", {added: addedItems.value, removed: removedItems.value});
}

function change() {
  emit('input', valueList.value);
}

function handleDragStart (){
  isHovered.value = false
}

function handleDragEnd(){
  isHovered.value = true
}

function moveRight() {
  valueList.value = allItems.value;
  listValue.value = [];
  change();
}

function moveLeft() {
  listValue.value = allItems.value;
  valueList.value = [];
  change();
}

const getVisibilityMap = (query: string, type: "left" | "right") => {
  if(props.paginated) {
    clearTimeout(bounceTimer);
    bounceTimer = setTimeout(() => {
      if(type === "left") {
        stateLeft.reset();  
        emit('search-left', {search: currentSearchLeft.value, state: stateLeft});
      } else {
        stateRight.reset();  
        emit('search-right', {search: currentSearchRight.value, state: stateRight});
      }
    }, 500)
    
    return;
  }
  const allItemsFlat = allItems.value.flat();
   return new Set(
     allItemsFlat.filter((item: types.ArrayValue) => {
        const itemText = item.text.toLowerCase();
        const currentSearchLeftLower = query.toLowerCase();
        return currentSearchLeftLower ? itemText.indexOf(currentSearchLeftLower) > -1 : false;
      }
    )
  )
}


return { __sfc: true,props, emit, isHovered, currentSearchLeft, currentSearchRight, visibilityPlanLeft, visibilityPlanRight, listValueRef, valueListRef, listValue, valueList, allItems, listValueSearch, valueListSearch, isListValueEmpty, isValueListEmpty, paginationLeftRef, paginationRightRef, stateLeft, stateRight, bounceTimer, addedItems, removedItems, leftSideHandleIntersection, rightSideHandleIntersection, elementAddedRemoved, elementClicked, change, handleDragStart, handleDragEnd, moveRight, moveLeft, getVisibilityMap, ButtonCommon, draggable, SearchBar, LoadingSpinner }
}

})